import { Card, CardBody } from "@heroui/react";
import { MetricValue } from "./MetricValue";
import { Info } from "lucide-react";
import { IssuesList } from "./MetricsIssueList";
import { GeminiAnalysisResult } from "./types";

const mockMetrics = {
  transcription_score: 0.85,
  silences_score: 0.7,
  objective_met: true,
  final_call_score: 0.8,
  response_time_score: 0.75,
  call_analysis: {
    sentiment_score: 65,
    customer_demographics: {
      age_range: "middle_aged",
      gender: "female",
      confidence: 0.8,
    },
    issues: [
      {
        type: "background_noise",
        startSec: 45,
        endSec: 52,
        detail: "Background noise detected during customer explanation",
        severity: "medium",
      },
      {
        type: "user_interruption",
        startSec: 120,
        endSec: 125,
        detail: "Customer interrupted agent response",
        severity: "low",
      },
    ],
    speaker_changes: 12,
    user_interruption_count: 2,
    agent_interruption_count: 1,
  },
};

export default function Metrics() {
  const metrics = mockMetrics;

  const backgroundNoiseCount = metrics.call_analysis.issues.filter(
    (issue) => issue.type === "background_noise",
  ).length;

  const repetitionCount = metrics.call_analysis.issues.filter(
    (issue) => issue.type === "agent_repetition",
  ).length;

  return (
    <div className="relative">
      <div className="blur-sm pointer-events-none">
        <div className="flex flex-col gap-2">
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-x-3 gap-y-2 md:grid-cols-2">
              <div className="p-4">
                <div className="flex flex-col gap-3">
                  <span className="text-sm font-medium">Audio Quality</span>
                  <MetricValue
                    label="background noise"
                    value={backgroundNoiseCount + 1}
                    checkpoints={{
                      medium: 4,
                      high: 5,
                      max: 6,
                    }}
                  />
                  <MetricValue
                    label="transcription quality"
                    value={metrics.transcription_score}
                    flipped
                    checkpoints={{
                      medium: 0.5,
                      high: 0.2,
                      max: 1,
                    }}
                  />
                </div>
              </div>

              <div className="p-4">
                <div className="flex flex-col gap-3">
                  <span className="text-sm font-medium">Conversation Flow</span>
                  <MetricValue
                    label="interruption"
                    value={metrics.call_analysis.user_interruption_count}
                    showValueInLabel
                    checkpoints={{
                      medium: 3,
                      high: 4,
                      max: 5,
                    }}
                  />
                  <MetricValue
                    label="speaker change"
                    value={metrics.call_analysis.speaker_changes}
                    showValueInLabel
                    checkpoints={{
                      medium: 3,
                      high: 4,
                      max: 5,
                    }}
                  />
                </div>
              </div>

              <div className="p-4">
                <div className="flex flex-col gap-3">
                  <span className="text-sm font-medium">Agent Performance</span>
                  <MetricValue
                    label="response latency"
                    value={1 - metrics.response_time_score}
                    checkpoints={{
                      medium: 0.6,
                      high: 0.4,
                      max: 1,
                    }}
                  />
                  <MetricValue
                    label="repetition"
                    value={repetitionCount}
                    showValueInLabel
                    checkpoints={{
                      medium: 2,
                      high: 3,
                      max: 5,
                    }}
                  />
                </div>
              </div>

              <div className="p-4">
                <div className="flex flex-col gap-3">
                  <span className="text-sm font-medium">Engagement</span>
                  <MetricValue
                    label="sentiment"
                    value={metrics.call_analysis.sentiment_score}
                    flipped
                    checkpoints={{
                      medium: 40,
                      high: 20,
                      max: 100,
                    }}
                  />
                  <MetricValue
                    label="politeness"
                    value={85}
                    checkpoints={{
                      medium: 80,
                      high: 90,
                      max: 100,
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-semibold">Language</span>
                  <p>English</p>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-semibold">Caller Profile</span>
                  <p className="capitalize">
                    {metrics.call_analysis.customer_demographics.age_range.replace(
                      "_",
                      " ",
                    )}
                    , {metrics.call_analysis.customer_demographics.gender}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-semibold">
                    Total Utterances
                  </span>
                  <p>24</p>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-semibold">User Word Count</span>
                  <p>187</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                Issues
              </h3>
              <IssuesList
                issues={
                  metrics.call_analysis.issues as GeminiAnalysisResult["issues"]
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute inset-0 flex items-center justify-center">
        <Card className="shadow-lg rounded-md">
          <CardBody className="px-4 py-5 text-center">
            <div className="flex gap-2 h-fit">
              <div className="p-1.5 bg-gray-200 rounded-full h-fit">
                <Info className="h-4 w-4 text-gray-600" />
              </div>
              <div className="ml-1 flex flex-col items-start gap-1">
                <h3 className="text-base font-medium">Metrics Not Available</h3>
                <p className="text-xs text-gray-600">
                  Metrics were not extracted for this call
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
